/**
 * CSRF (Cross-Site Request Forgery) Protection
 * Generates and validates tokens to prevent forged requests
 */

const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_TOKEN_TIMESTAMP_KEY = 'csrf_token_timestamp';
const TOKEN_EXPIRY_MS = 3600000; // 1 hour

/**
 * Generate a new CSRF token and store it in sessionStorage
 * Should be called on app initialization
 */
export function generateCSRFToken(): string {
  const token = crypto.randomUUID();
  const timestamp = Date.now().toString();
  
  sessionStorage.setItem(CSRF_TOKEN_KEY, token);
  sessionStorage.setItem(CSRF_TOKEN_TIMESTAMP_KEY, timestamp);
  
  return token;
}

/**
 * Get the current CSRF token from sessionStorage
 * Returns null if token doesn't exist or has expired
 */
export function getCSRFToken(): string | null {
  const token = sessionStorage.getItem(CSRF_TOKEN_KEY);
  const timestamp = sessionStorage.getItem(CSRF_TOKEN_TIMESTAMP_KEY);
  
  if (!token || !timestamp) {
    return null;
  }
  
  // Check if token has expired
  const tokenAge = Date.now() - parseInt(timestamp, 10);
  if (tokenAge > TOKEN_EXPIRY_MS) {
    // Token expired, clear it
    clearCSRFToken();
    return null;
  }
  
  return token;
}

/**
 * Validate a CSRF token against the stored token
 */
export function validateCSRFToken(token: string): boolean {
  const storedToken = getCSRFToken();
  
  if (!storedToken) {
    return false;
  }
  
  return token === storedToken;
}

/**
 * Clear the CSRF token from sessionStorage
 */
export function clearCSRFToken(): void {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
  sessionStorage.removeItem(CSRF_TOKEN_TIMESTAMP_KEY);
}

/**
 * Refresh the CSRF token (generate new one)
 */
export function refreshCSRFToken(): string {
  clearCSRFToken();
  return generateCSRFToken();
}

/**
 * Get CSRF token as header object for API requests
 */
export function getCSRFHeader(): Record<string, string> {
  const token = getCSRFToken();
  
  if (!token) {
    // Generate new token if none exists
    const newToken = generateCSRFToken();
    return { 'X-CSRF-Token': newToken };
  }
  
  return { 'X-CSRF-Token': token };
}
